/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toActivitySector } from '@data/dto/activity-sector.dto';
import { toSortParams } from '@data/dto/api.dto';
import {
  ActivitySectorHttp,
  ActivitySectorWizard,
} from '@data/models/activity-sector.interface';
import { ActivitySector } from '@data/models/activity-sector.interface';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class ActivitySectorService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly activitySectorEndpoint = '/activity-sector-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listActivitySectors(
    filters: FilterParams,
  ): Promise<PaginateResult<ActivitySector[]>> {
    const { data: response, error } = await this.API?.get<{
      data: ActivitySectorHttp[];
      meta: Meta;
    }>(this.activitySectorEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    const items = response?.data?.map(toActivitySector) || [];

    return {
      items,
      totalSize: items.length,
    };
  }

  public async upsertActivitySector({
    id,
    ...activitySectorWizard
  }: ActivitySectorWizard): Promise<ActivitySector | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<ActivitySectorWizard, 'id'> },
        { data: ActivitySectorHttp }
      >(this.activitySectorEndpoint, { data: activitySectorWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toActivitySector(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: ActivitySectorWizard },
      { data: ActivitySectorHttp }
    >(`${this.activitySectorEndpoint}/${id}`, {
      data: { ...activitySectorWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toActivitySector(data?.data || {});
  }

  public async deleteActivitySector(
    id: string,
  ): Promise<ActivitySector | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: ActivitySectorHttp;
    }>(`${this.activitySectorEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toActivitySector(data?.data || {});
  }

  public async sortActivitySector(
    sortParams: DatatableReorderParams<ActivitySector>,
  ): Promise<ActivitySector | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.activitySectorEndpoint}/${id}/sort`,
      {
        data: toSortParams('activity_sector_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
